import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import {
	toProjectType,
	type CUSTOMER_SERVICE_PROJECT,
	CORE_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	type ProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import {
	fireUIAnalytics,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import {
	CORE,
	PRODUCT_DISCOVERY,
	SERVICE_DESK,
	SOFTWARE,
} from '@atlassian/jira-shared-types/src/application-key.tsx';

export type SupportedProjectType =
	| typeof SERVICE_DESK_PROJECT
	| typeof PRODUCT_DISCOVERY_PROJECT
	| typeof SOFTWARE_PROJECT
	| typeof CUSTOMER_SERVICE_PROJECT
	| typeof CORE_PROJECT;
export const SUPPORTED_PROJECT_TYPES: SupportedProjectType[] = [
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
];

export const fireButtonAnalytics = (
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
	actionSubjectId: string,
	attributes?: AnalyticsAttributes,
) => {
	fireUIAnalytics(
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		}),
		actionSubjectId,
		{
			...attributes,
		},
	);
};

export const isSupportedProjectType = (value?: string | null): value is SupportedProjectType => {
	const projectType = toProjectType(value);

	return !!projectType && SUPPORTED_PROJECT_TYPES.includes(projectType);
};

export const isEligibleForJsmJpdTrialAwarenessV2 = (projectType: string): boolean =>
	toProjectType(projectType) === SERVICE_DESK_PROJECT ||
	toProjectType(projectType) === PRODUCT_DISCOVERY_PROJECT;

export const projectTypeToApplicationKey = (projectType: ProjectType) => {
	switch (projectType) {
		case SOFTWARE_PROJECT:
			return SOFTWARE;
		case SERVICE_DESK_PROJECT:
			return SERVICE_DESK;
		case CORE_PROJECT:
			return CORE;
		case PRODUCT_DISCOVERY_PROJECT:
			return PRODUCT_DISCOVERY;
		default:
			return CORE;
	}
};

const JIRA_SERVICE_MANAGEMENT_APP_NAME = 'Jira Service Management';
const JIRA_PRODUCT_DISCOVERY_APP_NAME = 'Jira Product Discovery';

export const getApplicationName = (projectType: ProjectType): string => {
	switch (projectType) {
		case SOFTWARE_PROJECT:
			return 'Jira';
		case SERVICE_DESK_PROJECT:
			return JIRA_SERVICE_MANAGEMENT_APP_NAME;
		case PRODUCT_DISCOVERY_PROJECT:
			return JIRA_PRODUCT_DISCOVERY_APP_NAME;
		default:
			throw new Error(`Unknown project type: ${projectType}`);
	}
};

export const getProductReferrerKey = (projectType: ProjectType): string | null => {
	if (projectType === SOFTWARE_PROJECT) {
		return 'jsw';
	}
	if (projectType === SERVICE_DESK_PROJECT) {
		return 'jsm';
	}
	if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		return 'jpd';
	}
	if (projectType === CORE_PROJECT) {
		return 'jwm';
	}
	return null;
};
